import { forwardRef, memo, MutableRefObject, Ref, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { debounce } from "lodash"
import { Box, useMediaQuery } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import FullCalendar from "@fullcalendar/react"
import luxonPlugin from "@fullcalendar/luxon3"
import timeGridPlugin from "@fullcalendar/timegrid"
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"
import interactionPlugin, { DateClickArg, EventResizeDoneArg } from "@fullcalendar/interaction"
import {
    AllowFunc,
    DateSelectArg,
    DatesSetArg,
    DateSpanApi,
    EventApi,
    EventClickArg,
    EventContentArg,
    EventDropArg,
    EventHoveringArg,
    EventMountArg,
    EventSourceInput,
} from "@fullcalendar/core"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import {
    applyDynamicTimeBuffer,
    applyEventBufferStyleBatch,
    getRoundedScrollTime,
    isAllDayAvailabilitySlot,
    isInPast,
    isShortTimelineEvent,
    isOngoing,
    isSameResources,
    isSlotFullyInPast,
    scrollToResource,
    SLOT_DURATION,
    SLOT_SIZE,
    toFloatingIsoMinutesFromJsDate,
    toHourMinuteFast,
    TOUCH_LONG_PRESS_DELAY_MS,
    WEEK_SLOT_MIN_WIDTH_MOBILE,
    withBatchRendering,
} from "./helpers"
import { selectSelectedLocale } from "../../../../../../modules/localization/localizationSelectors"
import { EnvironmentVariable } from "../../../../../../modules/environment/envTypes"
import {
    DEFAULT_DURATION_MINUTES,
    earliestTime,
    getEarliestSelectableStart,
    getMinAllowedEndTime,
    isLocalEvent,
} from "../resourceBookingCalendars/helpers"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { getEnvVariable } from "../../../../../../lib/getEnvVariable"
import { getFullCalendarConfig } from "./fullCalendarConfig"
import { DeliveryTimesField } from "../../form/formTypes"
import { useCalendarStyles } from "./useCalendarStyles"
import {
    ResourceBase,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
    ViewMode,
} from "./fullCalendarTypes"
import { GoToDatePicker } from "./GoToDatePicker"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAutoEdgeTimelineNavigation } from "./useAutoEdgeTimelineNavigation"
import { type TimelineVisibleRange, useTimelineVisibleRange } from "./useTimelineVisibleRange"
import { IsResourceAvailableFn } from "../resourceBookingCalendars/timelineHooks"
import { useIsMobile } from "../../../../../hooks/useIsMobile"

type StandardTimelineCalendarProps = Readonly<{
    viewMode: ViewMode
    isLoadingEvents?: boolean
    resources: TimelineCalendarResource[]
    resourceBufferMap?: Readonly<Record<string, number>>
    onDatesSet?: (arg: DatesSetArg) => void
    handleSelect?: (arg: DateSelectArg) => void
    /** Touch only: a tap on an empty slot, already widened to the default booking length. */
    handleDateTap?: (arg: DateSelectArg) => void
    handleEventDrop?: (arg: EventDropArg) => void
    resourceLabelContent: (arg: TimelineResourceLabelContent) => React.ReactNode
    resourceGroupLabelContent?: (arg: TimelineResourceGroupLabelContent) => React.ReactNode
    isResourceLaneLoading?: (extendedProps: ResourceBase) => boolean
    handleEventClick?: (arg: EventClickArg) => void
    handleEventResize?: (arg: EventResizeDoneArg) => void
    handleEventHover?: (arg: EventHoveringArg | null) => void
    eventSources: EventSourceInput[]
    localEvents?: TimelineCalendarEvent[]
    isBookedPeriod?: boolean
    isResourceAvailable?: IsResourceAvailableFn
    selectedDeliveries?: DeliveryTimesField
    isDraggingRef?: React.MutableRefObject<boolean>
    isUpdateMode?: boolean
    /** Receives the empty start chunk of the toolbar, for content rendered into it through a portal. */
    onToolbarStartSlotChange?: (slot: HTMLElement | null) => void
    onVisibleRangeChange?: (range: TimelineVisibleRange | null) => void
}>

const TOOLBAR_START_SLOT_SELECTOR = ".fc-header-toolbar .fc-toolbar-chunk:first-child"
// one `snapDuration` step — what a touch press without a drag selects
const SINGLE_SNAP_MS = 60 * 1000
// a touch selection shorter than one slot is a shaky press, not an intended range
const MIN_TOUCH_RANGE_MS = SLOT_DURATION * 60 * 1000

type TapPoint = Pick<DateSelectArg, "start" | "allDay" | "jsEvent" | "view" | "resource">

// forwardRef, not a `ref` prop: on React 18 `ref` never reaches a plain function component
const StandardTimelineCalendarInner = forwardRef<FullCalendar, StandardTimelineCalendarProps>(function StandardTimelineCalendar({
    viewMode,
    isLoadingEvents = false,
    resources,
    resourceBufferMap: resourceBufferMapProp,
    onDatesSet,
    handleSelect,
    handleDateTap,
    handleEventDrop,
    resourceLabelContent,
    resourceGroupLabelContent,
    isResourceLaneLoading,
    handleEventClick,
    handleEventResize,
    handleEventHover,
    eventSources,
    localEvents,
    isBookedPeriod = false,
    isResourceAvailable,
    selectedDeliveries,
    isDraggingRef,
    isUpdateMode = false,
    onToolbarStartSlotChange,
    onVisibleRangeChange,
}: StandardTimelineCalendarProps, ref: Ref<FullCalendar>) {
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const isDayGrid = viewMode === "daysGrid"
    const isMobile = useIsMobile()
    const isTouch = useMediaQuery("(pointer: coarse)")
    const { classes } = useCalendarStyles({
        isDayGrid,
        isEmptyResources: !resources.length,
        emptyResourcesText: l10n.getString("create-order-calendar-no-resources-message"),
    })
    const resourceLaneClassNames = useMemo(
        () =>
            // FullCalendar types extendedProps as Record<string, any>; we know it's ResourceBase
            // because we populate it ourselves when building TimelineCalendarResource objects.
            ({ resource }: { resource: { extendedProps: unknown } }) => {
                const extendedProps = resource.extendedProps as ResourceBase
                const names: string[] = []
                if (isResourceLaneLoading?.(extendedProps)) names.push(classes.laneSkeleton)
                if (extendedProps.accessControl?.hasAccess === false) names.push(classes.accessLimitedRow)
                return names
            },
        [isResourceLaneLoading, classes.laneSkeleton, classes.accessLimitedRow],
    )
    const resourceLabelClassNames = useMemo(
        () =>
            ({ resource }: { resource: { extendedProps: unknown } }) => {
                const extendedProps = resource.extendedProps as ResourceBase
                const names: string[] = [classes.resourceLabel]
                if (extendedProps.accessControl?.hasAccess === false) names.push(classes.accessLimitedRow)
                return names
            },
        [classes.resourceLabel, classes.accessLimitedRow],
    )
    const groupField = "location"
    const locale = useAppSelector(selectSelectedLocale)

    const setCalendarRef = useCallback(
        (cal: FullCalendar | null) => {
            calendarRef.current = cal
            if (typeof ref === "function") ref(cal)
            // React types mark RefObject.current readonly although React itself assigns to it
            else if (ref) (ref as MutableRefObject<FullCalendar | null>).current = cal
        },
        [ref],
    )

    const resourceBufferMapFallback = useMemo(
        () =>
            resourceBufferMapProp ??
            resources.reduce(
                (acc, r) => {
                    acc[r.id] = r.buffer ?? 0
                    return acc
                },
                {} as Record<string, number>,
            ),
        [resourceBufferMapProp, resources],
    )
    const calendarConfig = useMemo(
        () => getFullCalendarConfig(isDayGrid, l10n, calendarRef, dateTimeFactory, locale, isMobile),
        [dateTimeFactory, isDayGrid, l10n, locale, isMobile],
    )
    const debounceRef = useRef<ReturnType<typeof debounce> | null>(null)
    const pendingEventMountRef = useRef<EventMountArg[]>([])
    const rafScheduledRef = useRef(false)
    const hasScrolledRef = useRef(false)
    const initialDateRef = useRef<string | undefined>(
        (() => {
            const selectedEvent = localEvents?.find((el) => isLocalEvent(el.id))
            if (selectedEvent?.start) {
                return (
                    dateTimeFactory.fromISO(selectedEvent.start).startOf("day").minus({ days: 1 }).toISODate() ??
                    undefined
                )
            }
            return dateTimeFactory.now().minus({ days: 1 }).toISODate() ?? undefined
        })(),
    )
    const verticalScrollToResourceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const nowRef = useRef(dateTimeFactory.now())

    nowRef.current = dateTimeFactory.now()

    useAutoEdgeTimelineNavigation(dateTimeFactory, calendarRef, containerRef, isDayGrid)
    const refreshVisibleRange = useTimelineVisibleRange(containerRef, onVisibleRangeChange)

    // No deps: FullCalendar may rebuild its toolbar on any render.
    useLayoutEffect(() => {
        onToolbarStartSlotChange?.(containerRef.current?.querySelector<HTMLElement>(TOOLBAR_START_SLOT_SELECTOR) ?? null)
    })

    useLayoutEffect(() => () => onToolbarStartSlotChange?.(null), [onToolbarStartSlotChange])

    useEffect(() => {
        return () => {
            debounceRef.current?.cancel()
            if (verticalScrollToResourceTimerRef.current) {
                clearTimeout(verticalScrollToResourceTimerRef.current)
            }
        }
    }, [])

    const timelineScrollTime = useMemo(() => getRoundedScrollTime(dateTimeFactory), [dateTimeFactory])

    const handleEventMouseEnter = (arg: EventHoveringArg) => {
        if (isDraggingRef?.current) {
            handleEventHover?.(null)
            return
        }
        debounceRef.current?.cancel()
        debounceRef.current = debounce(() => {
            if (!isDraggingRef?.current) {
                handleEventHover?.(arg)
            }
        }, 450)
        debounceRef.current()
    }

    const handleEventMouseLeave = () => {
        debounceRef.current?.cancel()
        if (!isDraggingRef?.current) {
            handleEventHover?.(null)
        }
    }

    useEffect(() => {
        if (isBookedPeriod && localEvents?.length && calendarRef.current) {
            const selectedEvent = localEvents.find((el) => isLocalEvent(el.id))

            const dateTime = selectedEvent && dateTimeFactory.fromISO(selectedEvent.start)
            if (!dateTime) return
            const start = dateTime.startOf("hour")

            const calendarApi = calendarRef.current.getApi()
            withBatchRendering(calendarApi, () => {
                applyDynamicTimeBuffer(calendarApi, start)
                calendarApi.gotoDate(dateTime.toJSDate())
            })
        }
    }, [localEvents, isBookedPeriod])

    useEffect(() => {
        if (
            !localEvents?.length &&
            selectedDeliveries?.deliveryDate &&
            selectedDeliveries?.deliveryTimes?.length &&
            calendarRef.current
        ) {
            const date = dateTimeFactory.fromISO(selectedDeliveries.deliveryDate)
            if (date) {
                const calendarApi = calendarRef.current.getApi()
                withBatchRendering(calendarApi, () => {
                    calendarApi.gotoDate(date.toJSDate())
                    calendarApi.scrollToTime(earliestTime(selectedDeliveries.deliveryTimes))
                })
            }
        }
    }, [localEvents, selectedDeliveries])

    useEffect(() => {
        if (calendarRef.current && isDayGrid) {
            const calendarApi = calendarRef.current.getApi()
            const view = calendarApi.view

            handleDatesSet({
                view,
                start: view.currentStart,
                end: view.currentEnd,
                startStr: view.currentStart.toISOString(),
                endStr: view.currentEnd.toISOString(),
                timeZone: calendarApi.getOption("timeZone") ?? "local",
            })
        }
    }, [isDayGrid])

    const startInteraction = () => {
        if (isDraggingRef) isDraggingRef.current = true
        debounceRef.current?.cancel()
        handleEventHover?.(null)
    }

    const stopInteraction = () => {
        if (isDraggingRef) isDraggingRef.current = false
        debounceRef.current?.cancel()
        handleEventHover?.(null)
    }

    const handleAttemptInPast = useCallback(() => {
        enqueueSnackbar({
            variant: "warning",
            message: l10n.getString("create-order-past-booking-attempt-message"),
        })
    }, [l10n])

    const getEventDisplayContent = useCallback(
        (eventInfo: EventContentArg) => {
            const { event, isMirror } = eventInfo
            const { start, end, id, extendedProps } = event
            const { outOfService, canEdit, subject, isAllDayAvailabilitySlot: isAllDayFlag } = extendedProps

            if (isDayGrid) {
                // For very short events (e.g. 5-10 minutes), text content forces a larger min-content width,
                // making the event appear as wide as a full 15-minute slot. Hide the label for short events.
                if (isShortTimelineEvent(dateTimeFactory, isDayGrid, event, isMirror)) return ""

                if ((outOfService || canEdit) && subject) return subject
                if (!isLocalEvent(id)) return `#${id}`
            }

            const startDt = start ? dateTimeFactory.fromJSDate(start) : null
            const endDt = end ? dateTimeFactory.fromJSDate(end) : null

            const formattedTime = [
                startDt ? toHourMinuteFast(startDt) : "",
                endDt ? toHourMinuteFast(endDt) : "",
            ]
                .filter(Boolean)
                .join(" - ")

            if (isDayGrid) return formattedTime

            const isAllDaySlot =
                typeof isAllDayFlag === "boolean"
                    ? isAllDayFlag
                    : !!startDt && !!endDt && isAllDayAvailabilitySlot(dateTimeFactory, startDt, endDt)

            return isAllDaySlot ? l10n.getString("create-order-calendar-all-day-label") : formattedTime
        },
        [dateTimeFactory, isDayGrid, l10n],
    )

    const handleDatesSet = (arg: DatesSetArg) => {
        onDatesSet?.(arg)
        refreshVisibleRange()

        // if (!calendarRef.current || !isDayGrid || isBookedPeriod) return

        // const calendarApi = calendarRef.current.getApi()
        // const viewStart = dateTimeFactory.fromJSDate(arg.start)
        // const now = dateTimeFactory.now()
        // const today = now.startOf("day")
        // const isToday = viewStart.hasSame(today, "day")
        // const nowHour = now.startOf("hour")
        // calendarApi.setOption("slotMinTime", isToday ? nowHour.toFormat("HH:mm:ss") : "00:00:00")
    }

    const eventAllow = useCallback<AllowFunc>(
        (dropInfo: DateSpanApi, draggedEvent: EventApi | null) => {
            if (!draggedEvent) return true

            const { start, end, resource } = dropInfo
            const draggedResIds = draggedEvent.getResources().map((r) => r.id)
            const dropResIds = resource?.id ? [resource.id] : draggedResIds

            if ((resource?.extendedProps as ResourceBase | undefined)?.accessControl?.hasAccess === false) {
                return false
            }

            const isStartUnchanged = !!draggedEvent.start && !!start && draggedEvent.start.getTime() === start.getTime()

            if (isUpdateMode && isOngoing(draggedEvent, dateTimeFactory)) {
                const sameResources = isSameResources(draggedResIds, dropResIds)
                const isEndAllowed = !!end && dateTimeFactory.fromJSDate(end) >= getMinAllowedEndTime(dateTimeFactory)
                return !!(isStartUnchanged && sameResources && isEndAllowed)
            }

            if (isStartUnchanged) {
                // Resize (or a lane-only move): the start is not being changed, so a booking that has
                // already begun stays editable — only the end must stay a minimum step from now.
                if (!end || dateTimeFactory.fromJSDate(end) < getMinAllowedEndTime(dateTimeFactory)) return false
            } else if (isUpdateMode) {
                // Updating an existing order keeps the original rule: it may not be moved into the past.
                if (isInPast(start?.toISOString())) return false
            } else if (!start || dateTimeFactory.fromJSDate(start) < getEarliestSelectableStart(dateTimeFactory)) {
                // Creating: the minute that holds the now-indicator stays reachable, so a new booking can
                // be dropped right onto the line; the drop handler pulls it up to the next full minute.
                return false
            }

            if (isResourceAvailable) {
                return isResourceAvailable(
                    toFloatingIsoMinutesFromJsDate(dateTimeFactory, start),
                    toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!),
                    dropResIds,
                    draggedEvent.id,
                )
            }

            return true
        },
        [dateTimeFactory, isUpdateMode, isResourceAvailable],
    )

    const handleScrollToResource = () => {
        const selectedEvent = localEvents?.find((e) => isLocalEvent(e.id))
        const resourceId = selectedEvent?.resourceIds?.[0]
        if (!resourceId) return

        scrollToResource(resourceId)
    }

    /**
     * FullCalendar calls `eventsSet` after it commits events. That is more reliable than a timer-only
     * effect: `scrollTime` defaults to "now", and refetching server events can re-apply that scroll
     * unless we call `gotoDate`/`scrollToTime` here. Also, `eventsSet` may run only once — the old flow
     * waited for a second `eventsSet` after `hasScrolledRef` flipped, so vertical scroll to the
     * resource row often never ran.
     */
    const handleEventSet = () => {
        if (!isDayGrid) return

        const cal = calendarRef.current
        if (!cal) return

        if (!hasScrolledRef.current) {
            hasScrolledRef.current = true

            const selectedEvent = localEvents?.find((el) => isLocalEvent(el.id))
            const isoDateTime = selectedEvent?.start
            if (!isoDateTime) return

            const dateTime = dateTimeFactory.fromISO(isoDateTime).startOf("hour").minus({ hours: 1 })
            if (!dateTime.isValid) return

            const calendarApi = cal.getApi()

            if (verticalScrollToResourceTimerRef.current) {
                clearTimeout(verticalScrollToResourceTimerRef.current)
            }
            verticalScrollToResourceTimerRef.current = setTimeout(() => {
                verticalScrollToResourceTimerRef.current = null
                withBatchRendering(calendarApi, () => {
                    const eventDay = dateTimeFactory.fromISO(isoDateTime).startOf("day")
                    const desiredViewStart = eventDay.minus({ days: 1 })
                    const currentViewStart = dateTimeFactory.fromJSDate(calendarApi.view.currentStart)
                    if (!desiredViewStart.hasSame(currentViewStart, "day")) {
                        calendarApi.gotoDate(desiredViewStart.toJSDate())
                    }
                    calendarApi.scrollToTime(dateTime.toMillis() - desiredViewStart.toMillis())
                })
                handleScrollToResource()
            }, 300)
        }
    }

    const handleEventDidMount = useCallback(
        (info: EventMountArg) => {
            pendingEventMountRef.current.push(info)
            if (!rafScheduledRef.current) {
                rafScheduledRef.current = true
                requestAnimationFrame(() => {
                    const pending = pendingEventMountRef.current
                    pendingEventMountRef.current = []
                    rafScheduledRef.current = false
                    if (pending.length > 0) {
                        applyEventBufferStyleBatch(pending, resourceBufferMapFallback)
                    }
                })
            }
        },
        [resourceBufferMapFallback],
    )

    const selectAllow = useCallback((span: DateSpanApi) => {
        return (span.resource?.extendedProps as ResourceBase | undefined)?.accessControl?.hasAccess !== false
    }, [])

    const isSelectionAllowed = useCallback(
        (arg: DateSelectArg) => {
            if ((arg.resource?.extendedProps as ResourceBase | undefined)?.accessControl?.hasAccess === false) {
                return false
            }

            // A new booking may be selected across the now-indicator — `handleSelect` pulls such a start
            // up to the next full minute. Updating keeps the original rule.
            if ((isUpdateMode && isInPast(arg.startStr)) || isInPast(arg.endStr)) {
                handleAttemptInPast()
                return false
            }

            return !(
                isUpdateMode &&
                localEvents?.some((evt) => {
                    const start = dateTimeFactory.fromISO(evt.start)
                    const end = dateTimeFactory.fromISO(evt.end)
                    const now = dateTimeFactory.now()
                    return start <= now && now < end
                })
            )
        },
        [dateTimeFactory, handleAttemptInPast, isUpdateMode, localEvents],
    )

    const handleTap = useCallback(
        ({ start, allDay, jsEvent, view, resource }: TapPoint) => {
            if (!handleDateTap || isLoadingEvents) return

            const startDt = dateTimeFactory.fromJSDate(start)
            const endDt = startDt.plus({ minutes: DEFAULT_DURATION_MINUTES })
            const selection: DateSelectArg = {
                start,
                end: endDt.toJSDate(),
                startStr: dateTimeFactory.toApiISOMinutesFormat(startDt),
                endStr: dateTimeFactory.toApiISOMinutesFormat(endDt),
                allDay,
                jsEvent,
                view,
                resource,
            }

            if (isSelectionAllowed(selection)) handleDateTap(selection)
        },
        [dateTimeFactory, handleDateTap, isLoadingEvents, isSelectionAllowed],
    )

    const handleSelectCallback = useCallback(
        (arg: DateSelectArg) => {
            const durationMs = arg.end.getTime() - arg.start.getTime()

            if (isTouch && durationMs < MIN_TOUCH_RANGE_MS) {
                // a press without any drag also fires dateClick, which books it already
                if (durationMs > SINGLE_SNAP_MS) handleTap(arg)
                return
            }

            if (isSelectionAllowed(arg)) handleSelect?.(arg)
        },
        [handleSelect, handleTap, isSelectionAllowed, isTouch],
    )

    const handleDateClickCallback = useCallback(
        ({ date, allDay, jsEvent, view, resource }: DateClickArg) =>
            handleTap({ start: date, allDay, jsEvent, view, resource }),
        [handleTap],
    )

    const handleEventClickCallback = useCallback(
        (arg: EventClickArg) => {
            const { event } = arg

            if (isUpdateMode && isLocalEvent(event.id) && isOngoing(event, dateTimeFactory)) {
                return
            }

            if (!isDayGrid) debounceRef.current?.cancel()
            handleEventClick?.(arg)
        },
        [dateTimeFactory, handleEventClick, isDayGrid, isUpdateMode],
    )

    /**
     * An already started meeting may not be shortened to end sooner than the minimum step from now.
     * Checked against `oldEvent`, because `event` is already mutated here and would no longer look
     * ongoing once its end moved into the past. Reverts silently — the past-booking snackbar talks
     * about the start time and would be misleading for this rule.
     */
    const isOngoingEndShortening = useCallback(
        (arg: EventDropArg | EventResizeDoneArg) => {
            if (!isUpdateMode || !isOngoing(arg.oldEvent, dateTimeFactory)) return false

            const { end } = arg.event
            return !end || dateTimeFactory.fromJSDate(end) < getMinAllowedEndTime(dateTimeFactory)
        },
        [dateTimeFactory, isUpdateMode],
    )

    const handleEventDropCallback = useCallback(
        (arg: EventDropArg) => {
            if (isDraggingRef) isDraggingRef.current = false
            if (isOngoingEndShortening(arg)) {
                arg.revert()
                return
            }
            if (
                !eventAllow(
                    {
                        start: arg.event.start!,
                        end: arg.event.end!,
                        startStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.start!),
                        endStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.end!),
                        allDay: arg.event.allDay ?? false,
                        resource: arg.newResource,
                    },
                    arg.event,
                )
            ) {
                arg.revert()
                handleAttemptInPast()
                return
            }
            handleEventDrop?.(arg)
        },
        [dateTimeFactory, eventAllow, handleAttemptInPast, handleEventDrop, isDraggingRef, isOngoingEndShortening],
    )

    const handleEventResizeCallback = useCallback(
        (arg: EventResizeDoneArg) => {
            if (isDraggingRef) isDraggingRef.current = false
            if (isOngoingEndShortening(arg)) {
                arg.revert()
                return
            }
            if (
                !eventAllow(
                    {
                        start: arg.event.start!,
                        end: arg.event.end!,
                        startStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.start!),
                        endStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.end!),
                        allDay: arg.event.allDay ?? false,
                        resource: arg.event.getResources()[0],
                    },
                    arg.event,
                )
            ) {
                arg.revert()
                handleAttemptInPast()
                return
            }
            handleEventResize?.(arg)
        },
        [dateTimeFactory, eventAllow, handleAttemptInPast, handleEventResize, isDraggingRef, isOngoingEndShortening],
    )

    const getEventClassNames = useCallback(
        (arg: { event: EventApi; isMirror: boolean }) => {
            const classNames = ["custom-event"]

            if (isShortTimelineEvent(dateTimeFactory, isDayGrid, arg.event, arg.isMirror)) {
                classNames.push("custom-event--short")
            }

            // Week view: grey out slots that are fully in the past (incl. today's past slots)
            if (!isDayGrid && isSlotFullyInPast(dateTimeFactory, arg.event.end, nowRef.current)) {
                classNames.push("custom-event--past")
            }

            return classNames
        },
        [dateTimeFactory, isDayGrid],
    )

    return (
        <Box ref={containerRef} className={classes.container}>
            <FullCalendar
                ref={setCalendarRef}
                plugins={[resourceTimelinePlugin, timeGridPlugin, luxonPlugin, interactionPlugin]}
                schedulerLicenseKey={getEnvVariable(EnvironmentVariable.FULLCALENDAR_TOKEN)}
                {...calendarConfig}
                initialDate={initialDateRef.current}
                progressiveEventRendering
                resourceGroupField={groupField}
                resources={resources}
                eventSources={eventSources}
                resourceLabelContent={resourceLabelContent}
                resourceLabelClassNames={resourceLabelClassNames}
                resourceLaneClassNames={resourceLaneClassNames}
                resourceGroupLabelContent={resourceGroupLabelContent}
                resourceGroupLabelClassNames={classes.resourceGroupLabel}
                eventContent={getEventDisplayContent}
                eventDidMount={isDayGrid ? handleEventDidMount : undefined}
                eventClassNames={getEventClassNames}
                selectable={!isLoadingEvents && isDayGrid}
                selectAllow={selectAllow}
                eventsSet={() => isDayGrid && handleEventSet()}
                select={handleSelectCallback}
                dateClick={isTouch && isDayGrid ? handleDateClickCallback : undefined}
                {...(isTouch ? { longPressDelay: TOUCH_LONG_PRESS_DELAY_MS } : {})}
                editable={!isLoadingEvents && isDayGrid}
                eventDrop={handleEventDropCallback}
                eventAllow={eventAllow}
                eventDragStart={startInteraction}
                eventDragStop={stopInteraction}
                eventResizeStart={startInteraction}
                eventResizeStop={stopInteraction}
                titleFormat={"{LLLL {d}}, yyyy"}
                resourceOrder="location,type.name,title"
                scrollTime={timelineScrollTime}
                scrollTimeReset={!isDayGrid}
                locale={locale}
                resourceAreaHeaderContent=""
                slotDuration={isDayGrid ? `00:${SLOT_DURATION}:00` : { days: 1 }}
                snapDuration={isDayGrid ? "00:01:00" : undefined}
                slotMinWidth={isDayGrid ? SLOT_SIZE : isMobile ? WEEK_SLOT_MIN_WIDTH_MOBILE : undefined}
                eventMinWidth={isDayGrid ? 0 : undefined}
                datesSet={handleDatesSet}
                eventClick={handleEventClickCallback}
                eventMouseEnter={isTouch ? undefined : handleEventMouseEnter}
                eventMouseLeave={handleEventHover && !isTouch ? handleEventMouseLeave : undefined}
                eventResize={handleEventResizeCallback}
                nowIndicator={isDayGrid}
            />
            <GoToDatePicker calendarRef={calendarRef} />
        </Box>
    )
})

export const StandardTimelineCalendar = memo(StandardTimelineCalendarInner)
